import { useMemo, useState } from 'react';

import { Box, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { useQuery } from 'react-query';
import {
  getProfitabilityDataCharts,
  getProfitabilityTableColumns,
} from 'src/services/operation/operationFidc';
import { Profitability } from 'src/types/operations/fidc';

import {
  fakeProfitabilityData,
  getPeriodDate,
  normalizeString,
} from '../../DailyQuotaCharts/utils';
import { GroupButtonPeriod, Period } from '../GroupButtonPeriod';

type TransformedRow = {
  data: string;
  [key: string]: string | number;
};

const defaultPeriodInDays = 180;

const isUnicaCota = (cota: string): boolean => {
  return normalizeString(cota) === 'unica';
};

const getColumnName = (cota: string, serie: number | null): string => {
  if (isUnicaCota(cota)) {
    return 'Única';
  }

  if (serie === null || serie === 0) {
    return `${cota} Única`;
  }

  return `${cota} ${serie}`;
};

type ProfitabilityTableProps = {
  hashId: string;
  hasPermission: boolean;
};

export function ProfitabilityTable({
  hashId,
  hasPermission,
}: ProfitabilityTableProps) {
  const [selectedPeriod, setSelectedPeriod] =
    useState<Period>(defaultPeriodInDays);

  const { data: columnsData, isLoading: isLoadingColumns } = useQuery({
    queryKey: ['profitability-table-columns', hashId],
    queryFn: () => getProfitabilityTableColumns(hashId),
    enabled: !!hashId,
  });

  const { data: rawData, isLoading: isLoadingData } = useQuery({
    queryKey: ['profitability-table-data', hashId, selectedPeriod],
    queryFn: () =>
      getProfitabilityDataCharts(hashId, getPeriodDate(selectedPeriod)),
    enabled: !!hashId && !isLoadingColumns,
  });

  const data = hasPermission ? rawData : fakeProfitabilityData;

  const transformedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const groupedByDate = data.reduce(
      (acc, item: Profitability) => {
        if (!acc[item.data]) {
          acc[item.data] = { data: item.data };
        }

        const columnKey = getColumnName(item.cota, item.serie);
        acc[item.data][columnKey] = item.rentabilidadeDiaria;
        return acc;
      },
      {} as Record<string, TransformedRow>,
    );

    return Object.values(groupedByDate).sort(
      (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime(),
    );
  }, [data]);

  const columns = useMemo<MRT_ColumnDef<TransformedRow>[]>(() => {
    const cols: MRT_ColumnDef<TransformedRow>[] = [
      {
        header: 'Data',
        accessorKey: 'data',
        size: 120,
        Cell: ({ cell }) => {
          const dateValue = cell.getValue<string>();
          return dateValue
            ? format(parseISO(dateValue), 'dd/MM/yyyy', { locale: ptBR })
            : '-';
        },
      },
    ];

    if (columnsData && columnsData.length > 0) {
      columnsData.forEach((column) => {
        cols.push({
          header: column.column_name,
          accessorKey: column.column_name,
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            const formattedValue =
              value !== undefined && value !== null
                ? value.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  }) + '%'
                : '-';
            return (
              <Tooltip
                arrow
                title={value !== undefined && value !== null ? value : '-'}>
                <Typography fontSize={14}>{formattedValue}</Typography>
              </Tooltip>
            );
          },
        });
      });
    }

    return cols;
  }, [columnsData]);

  const handlePeriodChange = (days: Period) => {
    setSelectedPeriod(days);
  };

  const table = useMaterialReactTable({
    columns,
    data: transformedData ?? [],
    enableBottomToolbar: true,
    enableGlobalFilterModes: true,
    enableRowVirtualization: true,
    enablePagination: true,
    enableStickyHeader: true,
    enableRowNumbers: false,
    enableSorting: true,
    enableFullScreenToggle: false,
    initialState: {
      sorting: [{ id: 'data', desc: true }],
      isLoading: isLoadingData,
      pagination: {
        pageSize: 25,
        pageIndex: 0,
      },
    },
    state: {
      isLoading: isLoadingData,
    },
    localization: {
      ...MRT_Localization_PT_BR,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
    },
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableContainerProps: {
      sx: {
        height: 420,
      },
    },
  });

  return (
    <Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        gap={2}
        alignItems="flex-end">
        <GroupButtonPeriod
          selectedPeriod={selectedPeriod}
          handlePeriodChange={handlePeriodChange}
        />
      </Box>
      <MaterialReactTable table={table} />
    </Box>
  );
}
